import { removeInitialContent } from "@escapio/shared/components/element-builder";
import { data } from "@escapio/shared/state/payload";
import { html, nothing, render } from "lit-html";
import { classMap } from "lit-html/directives/class-map.js";
import { repeat } from "lit-html/directives/repeat.js";
import connect from "@escapio/shared/data-store/connect";
import { nextFrame } from "@escapio/shared/event/next-frame";
import { checkVisibility } from "@escapio/shared/event/visibility-observer";
import { translate } from "@escapio/shared/l10n/translate";
import { translateHtml } from "@escapio/shared/l10n/translateHtml";
import { isAtLeast, SIZE_TABLET } from "../../dom/screen";
import "../ads/ResultAdsBanner";
import "../ads/stroer/BannerForList";
import { format } from "../hotel/FilterToHashMapper";
import { addHash } from "../hotel/add-hash";
import {
	store,
	ACTION_HOTEL_VISIBLE_FOR_PICTURES,
	ACTION_FETCH_CONTEXT_CRITERIA,
} from "./DataStore";
import "./Hotel";
import { trackNoPrice } from "./tracker";
import { watchPictureVisibility } from "./watch-picture-visibility";
import { belongsEDLOnlyGroup } from "./sorting/SortForHotelList";
import { travelData } from "@escapio/portal/state/travelData";
import { hasDirectLink } from "@escapio/portal/state/DirectLink";

customElements.define(
	"esc-hotel-list",
	class extends connect(removeInitialContent(HTMLElement), store) {
		connectedCallback() {
			watchPictureVisibility(
				this,
				store.dispatch,
				ACTION_HOTEL_VISIBLE_FOR_PICTURES,
			);

			const hotelDealsJSON = this.getAttribute("hotel-deals");
			const articleDealsJSON = this.getAttribute("article-deals");
			if (hotelDealsJSON || articleDealsJSON) {
				this.deal = html`<esc-hotel-list-deals
					hotel-deals=${hotelDealsJSON}
					article-deals=${articleDealsJSON}
					event-label=${this.getAttribute("deal-event-label")}
				></esc-hotel-list-deals>`;
			}
			super.connectedCallback();
		}

		update(state, { visiblePage, isLoading, counters }) {
			const { filters } = state;
			const hotels = visiblePage;
			if (hotels.length > 0) {
				this.renderList(hotels, state, counters.nearby);
				if (
					hotels.length === 1 &&
					hotels[0].state.prices.hasLoadedWithoutOffers &&
					hasDirectLink(hotels[0])
				) {
					trackNoPrice("Liste nur EDL", filters);
				}
			} else if (!isLoading) {
				this.renderEmpty();
				trackNoPrice("Liste leer", filters);
			}
		}

		renderEmpty() {
			render(
				html`${this.deal ? this.deal : nothing}
					<div class="message message--colored message--top-space">
						<span
							class="fa fa-exclamation bullit bullit--blue message__icon"
						></span>
						<span>${translate("hotel_list_error_update_info")}</span>
					</div>`,
				this,
			);
		}

		renderList(elements, state, nearbyCount) {
			const { filters, sorting, topHotelId, adminToolsShowHotelInfo } = state;
			const hash = format(filters);

			// @TODO move to hotel widget and merge update methods
			let filteredHotelTypes = filters.hotelTypes;
			if (data.preselectedHotelType) {
				filteredHotelTypes = filteredHotelTypes.concat([
					data.preselectedHotelType,
				]);
			}

			const hotelCount = elements.length;

			const ADS = [
				{
					index: 3,
					ads: [
						{
							id: "result-ads",
							bannerElement: html`<esc-result-ads-banner></esc-result-ads-banner>`,
						},
					],
				},
				...(this.showStroerAd
					? [
							{
								index: 7,
								ads: [
									{
										id: "stroer-tablet-top",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="banner"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
										></esc-stroer-banner-for-list>`,
									},
									{
										id: "stroer-mobile-top",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="topmobile"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
										></esc-stroer-banner-for-list>`,
									},
								],
							},
							{
								index: 12,
								ads: [
									{
										id: "stroer-tablet-middle",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="banner2"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
										></esc-stroer-banner-for-list>`,
									},
									{
										id: "stroer-mobile-middle",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="topmobile2"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
										></esc-stroer-banner-for-list>`,
									},
								],
							},
							{
								index: 17,
								ads: [
									{
										id: "stroer-tablet-bottom",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="banner3"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--tablet"
										></esc-stroer-banner-for-list>`,
									},
									{
										id: "stroer-mobile-bottom",
										bannerElement: html`<esc-stroer-banner-for-list
											slot-name="topmobile3"
											class="hotel-list__ads-content-banner hotel-list__ads-content-banner--mobile"
										></esc-stroer-banner-for-list>`,
									},
								],
							},
						]
					: []),
			];

			ADS.filter(Boolean)
				.sort(({ index: a }, { index: b }) => Math.sign(b - a))
				.forEach(({ index, ads }) => {
					if (hotelCount > index) {
						elements.splice(index, 0, ...ads);
					}
				});

			const edlOnlyIndex = elements.findIndex(
				hotel =>
					!hotel.bannerElement &&
					belongsEDLOnlyGroup(sorting, topHotelId)(
						hotel,
						{ [hotel.id]: hotel.state.prices },
						travelData.hasDates,
					),
			);
			if (edlOnlyIndex > -1) {
				elements.splice(edlOnlyIndex, 0, {
					id: "hotels-edl-only",
					bannerElement: html`
						<div class="hotel-list-item hotels-group-header">
							<div>${translate("hotels_edl_only_heading")}</div>
						</div>
					`,
				});
			}

			const inTheNearIndex = elements.findIndex(
				({ distance }) => distance > -1,
			);
			if (inTheNearIndex > -1) {
				const headline =
					nearbyCount === 1
						? translateHtml("hotels_in_the_near_heading_singular")
						: translateHtml(
								"hotels_in_the_near_heading_plural",
								html`<strong>${nearbyCount}</strong>`,
							);
				elements.splice(inTheNearIndex, 0, {
					id: "hotels-in-the-near",
					bannerElement: html`
						<div class="hotel-list-item hotels-group-header">
							<div>${headline}</div>
						</div>
					`,
				});
			}

			if (this.deal) {
				const dealsIndex = elements.findIndex(
					({ state }) => !state?.isTopHotel && !state?.isTopEscapioDeal,
				);
				elements.splice(dealsIndex >= 0 ? dealsIndex : elements.length, 0, {
					id: "deals",
					bannerElement: this.deal,
				});
			}

			render(
				html`
					${repeat(
						elements,
						({ id }) => id,
						(element, index) => {
							if (element.bannerElement) {
								return element.bannerElement;
							}

							const classes = classMap({
								"hotel-list-item": true,
								"hotel-list-item--highlight": element.state.isTopHotel,
								"content-visibility-auto": index > 3,
							});

							return [
								html`<esc-hotel
									class=${classes}
									.hotel=${addHash(hash)(element)}
									.travelData=${travelData}
									.hotelTypeFilter=${filteredHotelTypes}
									.listSorting=${sorting}
									?exclusive=${element.exclusive}
								></esc-hotel>`,
								adminToolsShowHotelInfo
									? html`<esc-admin-tools-hotel-info
											.hotel=${element}
										></esc-admin-tools-hotel-info>`
									: nothing,
							];
						},
					)}
				`,
				this,
				{ host: this },
			);

			nextFrame().then(checkVisibility);
			if (isAtLeast(SIZE_TABLET)) {
				nextFrame().then(() =>
					store.dispatch(
						ACTION_FETCH_CONTEXT_CRITERIA,
						elements.map(({ id }) => id).filter(id => typeof id === "number"),
					),
				);
			}
		}

		get showStroerAd() {
			return this.hasAttribute("show-stroer-ad");
		}
	},
);
